import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import {
  Box,
  Flex,
  Text,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  IconButton,
  Button,
  Spinner,
  Badge,
  Divider,
  Image,
  useDisclosure,
  RadioGroup,
  Radio,
  HStack,
} from "@chakra-ui/react";
import { FaSearch, FaChevronLeft, FaChevronRight, FaEye } from "react-icons/fa";
import { FiDownload, FiRefreshCw } from "react-icons/fi";

import { ZoomImageModal } from "../../components/ZoomImageModal/ZoomImageModal";
import apiClient from "Configuration/apiClient";
import API_CONFIG from "../../Configuration/apiConfig";
import { getCurrentDate, getFirstDayOfMonth } from "../../Utils/dateUtils";
import { generatePresignedUrl, downloadImageAsFile } from "../../Utils/s3Utils";
import { useDateFilter } from "../../Hooks/useDateFilter";
import { SCREEN_KEYS } from "../../constants/screenKeys";

function fileNameOf(imagePath) {
  if (!imagePath) return "No Image";
  const parts = imagePath.split("/");
  return parts[parts.length - 1];
}

const IMAGE_TYPE_OPTIONS = {
  COOLER: "cooler",
  ACTIVATION: "activation",
};

export default function ImageValidationQuality() {
  const [imageType, setImageType] = useState(IMAGE_TYPE_OPTIONS.COOLER);
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [imageUrls, setImageUrls] = useState({});

  const { isOpen, onOpen, onClose } = useDisclosure();

  const { fromDate, toDate, setFromDate, setToDate } = useDateFilter(
    SCREEN_KEYS.IMAGE_QUALITY_VALIDATION,
    { fromDate: getFirstDayOfMonth(), toDate: getCurrentDate() }
  );

  const fetchDebounceRef = useRef(null);
  const abortControllerRef = useRef(null);
  const fetchedPathsRef = useRef(new Set());

  const handleFromDateChange = (value) => {
    setFromDate(value);
    if (value > toDate) setToDate(value);
  };

  const handleToDateChange = (value) => {
    if (value < fromDate) return;
    setToDate(value);
  };

  const handleImageTypeChange = (value) => {
    setImageType(value);
  };

  useEffect(() => {
    if (!fromDate || !toDate) return;

    const endpoint =
      imageType === IMAGE_TYPE_OPTIONS.ACTIVATION
        ? API_CONFIG.ENDPOINTS.IMAGE_QUALITY_VALIDATION.UNIDENTIFIED_ACTIVATION_IMAGES
        : API_CONFIG.ENDPOINTS.IMAGE_QUALITY_VALIDATION.UNIDENTIFIED_COOLER_IMAGES;

    if (fetchDebounceRef.current) clearTimeout(fetchDebounceRef.current);

    fetchDebounceRef.current = setTimeout(async () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      setLoading(true);
      setError(null);

      const [fromYear, fromMonth, fromDay] = fromDate.split("-").map((v) => parseInt(v, 10));
      const [toYear, toMonth, toDay] = toDate.split("-").map((v) => parseInt(v, 10));

      const requestBody = {
        from: { date: fromDay, month: fromMonth, year: fromYear },
        to: { date: toDay, month: toMonth, year: toYear },
      };

      try {
        const res = await apiClient.post(endpoint, requestBody, {
          signal: controller.signal,
        });

        const list = Array.isArray(res.data) ? res.data : res.data ? [res.data] : [];
        const mapped = list.map((item) => ({
          imagePath: item.imagePath || null,
          shopNo: item.shopNo ?? "N/A",
          outletDetails: item.outletDetails || "N/A",
          auditorDetails: item.auditorDetails || "N/A",
          outletChannelClass: item.outletChannelClass || "N/A",
          productText: item.productText || "N/A",
        }));

        fetchedPathsRef.current = new Set();
        setImageUrls({});
        setRowData(mapped);
        setCurrentIndex(0);
      } catch (err) {
        if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") return;
        console.error("Failed to fetch image quality validation data:", err);
        setError("Failed to fetch data. Please try again.");
        setRowData([]);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, 300);

    return () => {
      if (fetchDebounceRef.current) clearTimeout(fetchDebounceRef.current);
    };
  }, [fromDate, toDate, imageType]);

  const filteredData = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return rowData;
    return rowData.filter((row) =>
      [
        row.outletDetails,
        row.auditorDetails,
        row.productText,
        row.outletChannelClass,
        String(row.shopNo),
        fileNameOf(row.imagePath),
      ]
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [rowData, searchText]);

  useEffect(() => {
    if (currentIndex >= filteredData.length) setCurrentIndex(0);
  }, [filteredData, currentIndex]);

  const handleSearchChange = (value) => {
    setSearchText(value);
    setCurrentIndex(0);
  };

  const loadImageUrl = useCallback(
    async (imagePath) => {
      if (!imagePath || fetchedPathsRef.current.has(imagePath)) return;
      fetchedPathsRef.current.add(imagePath);
      const [year, month] = toDate.split("-").map((v) => parseInt(v, 10));
      const url = await generatePresignedUrl(imagePath, { month, year });
      setImageUrls((prev) => ({ ...prev, [imagePath]: url }));
    },
    [toDate]
  );

  const retryImageUrl = useCallback(
    (imagePath) => {
      if (!imagePath) return;
      fetchedPathsRef.current.delete(imagePath);
      setImageUrls((prev) => {
        const next = { ...prev };
        delete next[imagePath];
        return next;
      });
      loadImageUrl(imagePath);
    },
    [loadImageUrl]
  );

  const currentItem = filteredData[currentIndex] || null;

  // Load the current image, plus quietly pre-fetch its neighbors so Prev/Next feels instant.
  useEffect(() => {
    [filteredData[currentIndex - 1], filteredData[currentIndex], filteredData[currentIndex + 1]]
      .filter(Boolean)
      .forEach((item) => {
        if (item.imagePath) loadImageUrl(item.imagePath);
      });
  }, [currentIndex, filteredData, loadImageUrl]);

  const centerUrl = currentItem ? imageUrls[currentItem.imagePath] : undefined;

  const goPrev = () => setCurrentIndex((i) => Math.max(0, i - 1));
  const goNext = () => setCurrentIndex((i) => Math.min(filteredData.length - 1, i + 1));

  const handleDownload = async () => {
    if (!currentItem || !centerUrl) return;
    const [year, month] = toDate.split("-").map((v) => parseInt(v, 10));
    const downloadUrl = await generatePresignedUrl(currentItem.imagePath, {
      month,
      year,
      disposition: "attachment",
    });
    downloadImageAsFile(downloadUrl || centerUrl, fileNameOf(currentItem.imagePath));
  };

  return (
    <Box
      minH="100vh"
      bg="gray.50"
      pt={{ base: "7vh", sm: "8vh", md: "9vh", lg: "10vh" }}
      px={{ base: 3, sm: 4, md: 6, lg: 8 }}
      pb={{ base: 4, sm: 5, md: 6 }}
    >
      {/* Header + Date filters */}
      <Flex
        direction={{ base: "column", sm: "row" }}
        justify="space-between"
        align={{ base: "flex-start", sm: "center" }}
        mb={{ base: 4, sm: 5, md: 6 }}
        gap={{ base: 3, sm: 0 }}
      >
        <Heading
          as="h1"
          fontSize={{ base: "20px", sm: "25px", md: "30px", lg: "35px" }}
          fontWeight="bold"
          color="gray.800"
        >
          Image Quality Validation
        </Heading>

        <Flex
          gap={{ base: 2, sm: 3 }}
          direction={{ base: "column", sm: "row" }}
          w={{ base: "100%", sm: "auto" }}
          align={{ base: "stretch", sm: "flex-end" }}
        >
          <Box w={{ base: "100%", sm: "auto" }}>
            <Text fontSize={{ base: "xs", sm: "sm", md: "md" }} color="gray.800" fontWeight="bold" mb={1} mx={2}>
              From Date
            </Text>
            <Box
              as="input"
              type="date"
              value={fromDate}
              max={toDate}
              onChange={(e) => handleFromDateChange(e.target.value)}
              w={{ base: "100%", md: "180px" }}
              p={{ base: "5px 8px", sm: "7px 11px", md: "8px 12px" }}
              borderRadius="8px"
              border="1px solid"
              borderColor="gray.300"
              bg="white"
              fontSize={{ base: "13px", sm: "15px", md: "16px" }}
              cursor="pointer"
              transition="all 0.2s"
              _hover={{ borderColor: "gray.400" }}
              _focus={{ outline: "none", borderColor: "red.500", boxShadow: "0 0 0 3px rgba(244, 0, 9, 0.3)" }}
            />
          </Box>

          <Box w={{ base: "100%", sm: "auto" }}>
            <Text fontSize={{ base: "xs", sm: "sm", md: "md" }} fontWeight="bold" mb={1} mx={2} color="gray.800">
              To Date
            </Text>
            <Box
              as="input"
              type="date"
              value={toDate}
              min={fromDate}
              onChange={(e) => handleToDateChange(e.target.value)}
              w={{ base: "100%", md: "180px" }}
              p={{ base: "5px 8px", sm: "7px 11px", md: "8px 12px" }}
              borderRadius="8px"
              border="1px solid"
              borderColor="gray.300"
              bg="white"
              fontSize={{ base: "13px", sm: "15px", md: "16px" }}
              cursor="pointer"
              transition="all 0.2s"
              _hover={{ borderColor: "gray.400" }}
              _focus={{ outline: "none", borderColor: "red.500", boxShadow: "0 0 0 3px rgba(244, 0, 9, 0.3)" }}
            />
          </Box>
        </Flex>
      </Flex>

      <Box
        bg="white"
        borderRadius={{ base: "6px", md: "8px" }}
        boxShadow="0 2px 8px rgba(0, 0, 0, 0.1)"
        p={{ base: 3, md: 4 }}
        mb={{ base: 4, sm: 5, md: 6 }}
      >
        <RadioGroup value={imageType} onChange={handleImageTypeChange}>
          <HStack spacing={6}>
            <Radio value={IMAGE_TYPE_OPTIONS.COOLER} colorScheme="red">
              <Text fontSize="sm" fontWeight="600" color="gray.700">
                Cooler Position
              </Text>
            </Radio>
            <Radio value={IMAGE_TYPE_OPTIONS.ACTIVATION} colorScheme="red">
              <Text fontSize="sm" fontWeight="600" color="gray.700">
                Activation
              </Text>
            </Radio>
          </HStack>
        </RadioGroup>
      </Box>

      {error && (
        <Text color="red.500" fontWeight="bold" mb={3}>
          {error}
        </Text>
      )}

      <Flex direction={{ base: "column", lg: "row" }} gap={{ base: 4, lg: 6 }} align="stretch">
        {/* Gallery panel */}
        <Box
          flex={{ lg: 1.6 }}
          bg="white"
          borderRadius={{ base: "6px", md: "8px" }}
          boxShadow="0 2px 8px rgba(0, 0, 0, 0.1)"
          p={{ base: 4, md: 6 }}
        >
          <Flex justify="space-between" align="center" mb={4} wrap="wrap" gap={2}>
            <Heading as="h2" fontSize={{ base: "md", md: "lg" }} color="gray.800">
              🖼️ Visual Gallery
            </Heading>
            <Badge colorScheme="red" fontSize="sm" px={3} py={1} borderRadius="full">
              {searchText ? `${filteredData.length} of ${rowData.length}` : `${rowData.length}`} Images
            </Badge>
          </Flex>

          <InputGroup mb={5}>
            <InputLeftElement pointerEvents="none">
              <FaSearch color="#A0AEC0" />
            </InputLeftElement>
            <Input
              placeholder="Search by filename, outlet, or auditor..."
              value={searchText}
              onChange={(e) => handleSearchChange(e.target.value)}
              bg="gray.50"
              borderRadius="10px"
            />
          </InputGroup>

          {loading ? (
            <Flex justify="center" align="center" h="300px">
              <Spinner size="xl" color="red.500" thickness="4px" />
            </Flex>
          ) : filteredData.length === 0 ? (
            <Flex justify="center" align="center" h="300px" color="gray.500" textAlign="center" px={4}>
              {rowData.length === 0
                ? `No ${
                    imageType === IMAGE_TYPE_OPTIONS.ACTIVATION ? "activation" : "cooler"
                  } images found for the selected date range.`
                : "No images match your search."}
            </Flex>
          ) : (
            <>
              <Text textAlign="center" fontSize="sm" color="gray.500" mb={2}>
                Image <Text as="span" fontWeight="bold" color="red.500">{currentIndex + 1}</Text> /{" "}
                {filteredData.length}
              </Text>

              <Flex align="center" justify="center" gap={{ base: 2, md: 4 }} mb={4}>
                <IconButton
                  aria-label="Previous image"
                  icon={<FaChevronLeft />}
                  onClick={goPrev}
                  isDisabled={currentIndex === 0}
                  borderRadius="full"
                  variant="outline"
                  colorScheme="red"
                  boxShadow="sm"
                  flexShrink={0}
                />

                <Box
                  position="relative"
                  w="100%"
                  maxW="460px"
                  h={{ base: "280px", md: "380px" }}
                  bg="gray.100"
                  borderRadius="lg"
                  overflow="hidden"
                  border="1px solid"
                  borderColor="gray.200"
                  boxShadow="0 8px 24px rgba(0, 0, 0, 0.08)"
                >
                  {!currentItem.imagePath ? (
                    <Flex h="100%" align="center" justify="center" color="gray.400" fontSize="sm">
                      No Image
                    </Flex>
                  ) : centerUrl === undefined ? (
                    <Flex h="100%" align="center" justify="center">
                      <Spinner size="lg" color="red.400" />
                    </Flex>
                  ) : centerUrl === null ? (
                    <Flex h="100%" align="center" justify="center" color="gray.400" fontSize="sm">
                      Image failed to load
                    </Flex>
                  ) : (
                    <Image src={centerUrl} alt="" w="100%" h="100%" objectFit="contain" />
                  )}

                  {currentItem.imagePath && (
                    <IconButton
                      aria-label="Reload image"
                      icon={<FiRefreshCw size={12} />}
                      size="sm"
                      position="absolute"
                      top="8px"
                      right="8px"
                      borderRadius="full"
                      bg="whiteAlpha.800"
                      _hover={{ bg: "white" }}
                      onClick={() => retryImageUrl(currentItem.imagePath)}
                    />
                  )}
                </Box>

                <IconButton
                  aria-label="Next image"
                  icon={<FaChevronRight />}
                  onClick={goNext}
                  isDisabled={currentIndex >= filteredData.length - 1}
                  borderRadius="full"
                  variant="outline"
                  colorScheme="red"
                  boxShadow="sm"
                  flexShrink={0}
                />
              </Flex>

              <Divider mb={4} />

              <Flex
                justify="space-between"
                align="center"
                wrap="wrap"
                gap={3}
                bg="gray.50"
                borderRadius="10px"
                p={3}
              >
                <Flex align="center" gap={2}>
                  <Box
                    w="8px"
                    h="8px"
                    borderRadius="full"
                    bg={
                      !currentItem?.imagePath
                        ? "gray.400"
                        : centerUrl === undefined
                        ? "yellow.400"
                        : centerUrl
                        ? "green.400"
                        : "red.400"
                    }
                  />
                  <Text fontWeight="bold" fontSize="sm" color="gray.800">
                    {fileNameOf(currentItem?.imagePath)}
                  </Text>
                </Flex>

                <Text fontSize="xs" color="gray.500" noOfLines={1}>
                  {currentItem?.outletDetails} • Shop No: {currentItem?.shopNo}
                </Text>
              </Flex>
            </>
          )}
        </Box>

        {/* Details panel */}
        <Box
          flex={{ lg: 1 }}
          bg="white"
          borderRadius={{ base: "6px", md: "8px" }}
          boxShadow="0 2px 8px rgba(0, 0, 0, 0.1)"
          p={{ base: 4, md: 6 }}
        >
          <Heading as="h2" fontSize={{ base: "md", md: "lg" }} color="gray.800" mb={4}>
            ℹ️ Image Details
          </Heading>

          {!currentItem ? (
            <Flex justify="center" align="center" h="200px" color="gray.400" textAlign="center">
              Select an image to view details
            </Flex>
          ) : (
            <>
              <Box
                position="relative"
                w="100%"
                h="220px"
                bg="gray.100"
                borderRadius="lg"
                overflow="hidden"
                mb={5}
              >
                {centerUrl === undefined ? (
                  <Flex h="100%" align="center" justify="center">
                    <Spinner size="lg" color="red.400" />
                  </Flex>
                ) : centerUrl === null ? (
                  <Flex h="100%" align="center" justify="center" color="gray.400">
                    Image failed to load
                  </Flex>
                ) : (
                  <Image src={centerUrl} alt="" w="100%" h="100%" objectFit="contain" />
                )}
                {centerUrl && (
                  <Button
                    position="absolute"
                    bottom="10px"
                    right="10px"
                    size="xs"
                    colorScheme="orange"
                    borderRadius="full"
                    leftIcon={<FaEye size={10} />}
                    onClick={onOpen}
                  >
                    View Full Screen
                  </Button>
                )}
              </Box>

              <Box mb={4}>
                <Text fontSize="xs" fontWeight="bold" color="gray.500" letterSpacing="wide" mb={1}>
                  OUTLET DETAILS
                </Text>
                <Text fontSize="lg" fontWeight="bold" color="gray.800">
                  {currentItem.outletDetails}
                </Text>
                <Text fontSize="sm" color="gray.500">
                  Shop No: {currentItem.shopNo}
                </Text>
                
              </Box>
              <Divider mb={1} />

              <Box mb={4}>
                <Text fontSize="xs" fontWeight="bold" color="gray.500" letterSpacing="wide" mb={1}>
                  AUDITOR DETAILS
                </Text>
                <Text fontSize="md" color="gray.800">
                  {currentItem.auditorDetails}
                </Text>
              </Box>

              <Divider mb={1} />

              <Box mb={4}>
                <Text fontSize="xs" fontWeight="bold" color="gray.500" letterSpacing="wide" mb={1}>
                  OUTLET CHANNEL CLASS
                </Text>
                <Badge colorScheme="red" variant="subtle" px={2} py={1} borderRadius="md">
                  {currentItem.outletChannelClass}
                </Badge>
              </Box>

              <Button
                w="100%"
                colorScheme="red"
                leftIcon={<FiDownload />}
                onClick={handleDownload}
                isDisabled={!centerUrl}
              >
                Download Image
              </Button>
            </>
          )}
        </Box>
      </Flex>

      <ZoomImageModal
        isOpen={isOpen}
        onClose={onClose}
        imageUrl={centerUrl}
        isLoading={centerUrl === undefined}
        title={currentItem?.outletDetails || "Image"}
        headerBg="red.500"
        headerColor="white"
        size="3xl"
        onDownload={handleDownload}
      />
    </Box>
  );
}
